// Helpers for the weekly availability editors (HH:mm values, 12-hour labels)
import { DayOfWeek, type TimeInterval } from '../../../models/Scheduling'

export interface TimeOption {
  value: string
  label: string
}

export const WEEKDAY_LABELS: Record<DayOfWeek, string> = {
  [DayOfWeek.SUNDAY]: 'Sunday',
  [DayOfWeek.MONDAY]: 'Monday',
  [DayOfWeek.TUESDAY]: 'Tuesday',
  [DayOfWeek.WEDNESDAY]: 'Wednesday',
  [DayOfWeek.THURSDAY]: 'Thursday',
  [DayOfWeek.FRIDAY]: 'Friday',
  [DayOfWeek.SATURDAY]: 'Saturday',
}

// Editors show the work week first
export const WEEKDAY_ORDER: DayOfWeek[] = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
]

export const formatTimeOptionLabel = (value: string): string => {
  const [hour, minute] = value.split(':').map(Number)
  const period = hour < 12 ? 'AM' : 'PM'
  const displayHour = hour % 12 === 0 ? 12 : hour % 12
  return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`
}

// "00:00" … "23:30" in 30-minute steps
export const TIME_OPTIONS: TimeOption[] = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2)
  const minute = index % 2 === 0 ? 0 : 30
  const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  return { value, label: formatTimeOptionLabel(value) }
})

const LAST_TIME_OPTION = TIME_OPTIONS[TIME_OPTIONS.length - 1].value

const DEFAULT_NEW_INTERVAL: TimeInterval = { start: '09:00', end: '17:00' }

export const sortIntervals = (intervals: TimeInterval[]): TimeInterval[] => [...intervals].sort((a, b) => a.start.localeCompare(b.start))

// Options never overlap siblings, but each row's current value stays selectable so legacy overlapping data keeps rendering
export const startOptionsFor = (intervals: TimeInterval[], index: number): TimeOption[] => {
  const previousEnd = intervals[index - 1]?.end
  const current = intervals[index]
  return TIME_OPTIONS.filter(
    (option) => option.value === current.start || ((previousEnd === undefined || option.value >= previousEnd) && option.value < current.end),
  )
}

export const endOptionsFor = (intervals: TimeInterval[], index: number): TimeOption[] => {
  const nextStart = intervals[index + 1]?.start
  const current = intervals[index]
  return TIME_OPTIONS.filter(
    (option) => option.value === current.end || (option.value > current.start && (nextStart === undefined || option.value <= nextStart)),
  )
}

const addOneHour = (value: string): string => {
  const [hour, minute] = value.split(':').map(Number)
  return `${String(hour + 1).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export const nextFreeInterval = (intervals: TimeInterval[]): TimeInterval | null => {
  if (intervals.length === 0) {
    return { ...DEFAULT_NEW_INTERVAL }
  }
  const latestEnd = intervals.reduce((latest, interval) => (interval.end > latest ? interval.end : latest), intervals[0].end)
  if (latestEnd >= LAST_TIME_OPTION) {
    return null
  }
  const end = addOneHour(latestEnd)
  return { start: latestEnd, end: end > LAST_TIME_OPTION ? LAST_TIME_OPTION : end }
}
